import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import './LoginPage.css';

interface LoginPageProps {
  onLogin: (email: string, password: string) => Promise<void>;
}

const LoginPage: React.FC<LoginPageProps> = ({ onLogin }) => {
  const { signup } = useAuth();
  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const isValidEmail = (v: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || v === 'admin';

  const switchMode = (m: 'login' | 'signup') => {
    setMode(m);
    setError('');
    setSuccess('');
    setPassword('');
    setConfirmPassword('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!email.trim()) return setError('Email is required');
    if (!isValidEmail(email)) return setError('Please enter a valid email address');
    if (!password) return setError('Password is required');
    if (password.length < 6) return setError('Password must be at least 6 characters');

    if (mode === 'signup') {
      if (password !== confirmPassword) return setError('Passwords do not match');
      setIsLoading(true);
      try {
        await signup(email, password, name.trim() || undefined);
        // signup auto-logs in via AuthContext
      } catch (err) {
        setError((err as Error).message || 'Signup failed. Please try again.');
      } finally {
        setIsLoading(false);
      }
    } else {
      setIsLoading(true);
      try {
        await onLogin(email, password);
      } catch (err) {
        setError((err as Error).message || 'Incorrect email or password.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <h1>Editorial Intelligence</h1>
            <p>Industrial Metallurgy Newsroom</p>
          </div>

          {/* Mode toggle */}
          <div className="login-tabs">
            <button
              type="button"
              className={`login-tab ${mode === 'login' ? 'active' : ''}`}
              onClick={() => switchMode('login')}
            >
              Sign In
            </button>
            <button
              type="button"
              className={`login-tab ${mode === 'signup' ? 'active' : ''}`}
              onClick={() => switchMode('signup')}
            >
              Create Account
            </button>
          </div>

          <form className="login-form" onSubmit={handleSubmit}>
            {error && (
              <div className="login-error">
                <span>⚠</span>
                <span>{error}</span>
              </div>
            )}
            {success && (
              <div className="login-success">
                <span>✓</span>
                <span>{success}</span>
              </div>
            )}

            {/* Name — signup only */}
            {mode === 'signup' && (
              <div className="form-group">
                <label htmlFor="name">Full Name (optional)</label>
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Your name"
                  disabled={isLoading}
                  autoFocus
                />
              </div>
            )}

            <div className="form-group">
              <label htmlFor="email">
                {mode === 'login' ? 'Email or Username' : 'Email Address'}
              </label>
              <input
                id="email"
                type="text"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder={mode === 'login' ? 'admin or you@example.com' : 'you@example.com'}
                disabled={isLoading}
                autoComplete={mode === 'login' ? 'username' : 'email'}
                autoFocus={mode === 'login'}
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder={mode === 'signup' ? 'At least 6 characters' : 'Enter your password'}
                disabled={isLoading}
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              />
            </div>

            {/* Confirm password — signup only */}
            {mode === 'signup' && (
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter your password"
                  disabled={isLoading}
                  autoComplete="new-password"
                />
              </div>
            )}

            {/* Remember me — login only */}
            {mode === 'login' && (
              <div className="form-footer">
                <label className="remember-me">
                  <input type="checkbox" />
                  <span>Remember me</span>
                </label>
              </div>
            )}

            <button type="submit" className="login-button" disabled={isLoading}>
              {isLoading ? (
                <><span className="spinner" />{mode === 'signup' ? 'Creating account…' : 'Signing in…'}</>
              ) : (
                mode === 'signup' ? 'Create Account' : 'Sign In to Newsroom'
              )}
            </button>

            <div className="login-divider"><span>or</span></div>

            <div className="signup-prompt">
              {mode === 'login' ? (
                <>New to AlCircle?{' '}
                  <button type="button" className="signup-link-btn" onClick={() => switchMode('signup')}>
                    Create an account
                  </button>
                </>
              ) : (
                <>Already have an account?{' '}
                  <button type="button" className="signup-link-btn" onClick={() => switchMode('login')}>
                    Sign in
                  </button>
                </>
              )}
            </div>
          </form>
        </div>

        <div className="login-footer">
          <p>AlCircle Editorial Intelligence Platform v2.4</p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
